import React from "react";
import {
  useRovingTabIndex,
  type UseRovingTabIndexResult,
} from "../primitives/index.js";

export interface UseActiveOptionInput {
  /** How many options the arrow keys move through. */
  readonly itemCount: number;
  /** A change here (the typed query) puts the active option back on the first. */
  readonly resetKey: string;
  readonly open: boolean;
  /** The listbox: its `[role="option"]` at the active index is kept in view. */
  readonly listRef: React.RefObject<HTMLElement | null>;
}

/**
 * The active option of a combobox's listbox: focus stays on the input and
 * the active option is pointed at with `aria-activedescendant`. A new query starts at the first
 * option, a shrinking list clamps the index to its last option, and the active
 * option scrolls into view.
 */
export function useActiveOption({
  itemCount,
  resetKey,
  open,
  listRef,
}: UseActiveOptionInput): UseRovingTabIndexResult {
  const roving = useRovingTabIndex({ itemCount });
  const { activeIndex, setActiveIndex } = roving;

  React.useEffect(() => {
    setActiveIndex(0);
  }, [resetKey, setActiveIndex]);

  React.useEffect(() => {
    if (activeIndex >= itemCount) {
      setActiveIndex(Math.max(itemCount - 1, 0));
    }
  }, [activeIndex, itemCount, setActiveIndex]);

  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const option =
      listRef.current.querySelectorAll<HTMLElement>('[role="option"]')[
        activeIndex
      ];
    option?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, listRef]);

  return roving;
}
